import { useMemo } from "react"
import type { SetupViewer } from "@/model/setup"
import { useProject } from "./project-context"

/** The first name of a display name ("Jannik Jung" → "Jannik"), the whole name when it has one word. */
export const firstName = (name: string): string => name.trim().split(/\s+/)[0] ?? name

/**
 * Polish 2026-10-23 §5: who reads a project page's setup banner, and whose each runner is (the runner list's owner and
 * the project roster's names), so "Lea's runner is offline" says "your runner" or "Jannik's runner".
 */
export const setupViewerOf = (
  userId: string,
  runners: ReadonlyArray<{ readonly runnerId: string; readonly ownerUserId: string }>,
  members: ReadonlyArray<{ readonly userId: string; readonly displayName: string }>,
): SetupViewer => ({
  userId,
  // decision 0166 §2: "Lea runs on Mira's machine", "Ask Mira to share"
  nameOf: (id) => {
    const name = members.find((m) => m.userId === id)?.displayName?.trim()
    return name ? firstName(name) : null
  },
  runnerOwner: (runnerId) => {
    const owner = runners.find((r) => r.runnerId === runnerId)?.ownerUserId
    if (!owner) return null
    const name = members.find((m) => m.userId === owner)?.displayName?.trim()
    return { userId: owner, name: name ? firstName(name) : null }
  },
})

export const useSetupViewer = (): SetupViewer => {
  const { userId, runners, state } = useProject()
  return useMemo(() => setupViewerOf(userId, runners, state.members), [userId, runners, state.members])
}
